import {createTaillightsWorker} from '@github-ui/taillights-worker/create-taillights-worker'
import type {
  DocumentOperation,
  DocumentOperations,
  DocumentResponse,
  PreparedBlock,
  ProjectRequest,
  RenderingProfile,
  SourceDocument,
  SourceSelection,
  WorkerDocumentMessage,
} from '@github-ui/taillights-worker/document'

export type TaillightsImage = {url: string; width: number; height: number}
export type TaillightsWorkerFactory = () => Worker

type Request = {
  type: DocumentOperation
  sent: boolean
  resolve: (response: DocumentResponse) => void
  reject: (error: Error) => void
}
type ImageRequest = PromiseWithResolvers<TaillightsImage> & {
  requestId?: number
  owners: number
  settled: boolean
  value?: TaillightsImage
  decoder?: HTMLImageElement
  url?: string
}

function abortError() {
  return new DOMException('The Taillights view no longer needs this request.', 'AbortError')
}

function errorFrom(value: unknown) {
  return value instanceof Error ? value : new Error(String(value))
}

let nextControllerId = 0

export class TaillightsController {
  readonly instanceId = ++nextControllerId
  readonly source: SourceDocument
  readonly profile: RenderingProfile
  readonly ready: Promise<DocumentOperations['initialize']['output']>
  readonly failure: Promise<Error>
  readonly #worker: Worker
  readonly #requests = new Map<number, Request>()
  readonly #images = new Map<string, ImageRequest>()
  readonly #urls = new Set<string>()
  readonly #failed = Promise.withResolvers<Error>()
  #nextRequestId = 0
  #terminalError: Error | null = null
  #closed = false

  constructor(
    source: SourceDocument,
    profile: RenderingProfile,
    workerFactory: TaillightsWorkerFactory = createTaillightsWorker,
  ) {
    this.source = source
    this.profile = profile
    this.#worker = workerFactory()
    this.failure = this.#failed.promise
    this.#worker.addEventListener('message', this.#receive)
    this.#worker.addEventListener('error', this.#crash)
    this.#worker.addEventListener('messageerror', this.#messageError)
    this.ready = this.#initialize()
    // A provider or an operation observes startup failure, not a global unhandled rejection.
    // eslint-disable-next-line github/no-then -- Keep the original rejecting promise stable for its consumers.
    void this.ready.catch(() => undefined)
  }

  get disposed() {
    return this.#closed
  }

  project(input: ProjectRequest) {
    return this.#request('project', input).promise
  }

  block(input: DocumentOperations['block']['input']) {
    return this.#request('block', input).promise
  }

  search(input: DocumentOperations['search']['input']) {
    return this.#request('search', input).promise
  }

  inspect(input: {selection: SourceSelection}) {
    return this.#request('inspect', input).promise
  }

  rects(input: DocumentOperations['rects']['input']) {
    return this.#request('rects', input).promise
  }

  image(block: PreparedBlock): Promise<TaillightsImage> {
    const cached = this.#images.get(block.imageKey)
    if (cached) return cached.promise
    const entry: ImageRequest = {
      ...Promise.withResolvers<TaillightsImage>(),
      owners: 0,
      settled: false,
    }
    this.#images.set(block.imageKey, entry)
    // An abandoned render may never attach a Suspense error handler.
    // eslint-disable-next-line github/no-then -- Preserve the stable rejecting promise for committed consumers.
    void entry.promise.catch(() => undefined)
    if (this.#terminalError || this.#closed) this.#failImage(entry, this.#terminalError ?? abortError())
    return entry.promise
  }

  decodedImage(block: PreparedBlock): TaillightsImage | null {
    return this.#images.get(block.imageKey)?.value ?? null
  }

  retain(block: PreparedBlock) {
    this.#paint(block)
    const entry = this.#images.get(block.imageKey)
    if (!entry) return
    entry.owners++
  }

  /** Paints and decodes blocks before they mount, so they show their colors from their first frame. */
  prefetch(blocks: readonly PreparedBlock[]) {
    return Promise.allSettled(blocks.map(block => this.#paint(block)))
  }

  #paint(block: PreparedBlock) {
    const promise = this.image(block)
    const entry = this.#images.get(block.imageKey)
    if (entry && entry.requestId === undefined && !entry.settled) {
      const request = this.#request('render', {blockId: block.id})
      entry.requestId = request.id
      void this.#present(entry, request.promise)
    }
    return promise
  }

  release(block: PreparedBlock) {
    const entry = this.#images.get(block.imageKey)
    if (!entry) return
    entry.owners = Math.max(0, entry.owners - 1)
    // Strict Effects reconnects an owner in the same turn. Completed results live until disposal.
    queueMicrotask(() => {
      if (
        entry.owners ||
        entry.settled ||
        entry.requestId === undefined ||
        !this.#requests.has(entry.requestId) ||
        this.#images.get(block.imageKey) !== entry
      )
        return
      this.#images.delete(block.imageKey)
      this.#failImage(entry, abortError())
      this.#cancel([entry.requestId])
    })
  }

  dispose() {
    if (this.#closed) return
    this.#closed = true
    this.#cancel([...this.#requests.keys()])
    this.#stop(abortError())
    for (const url of this.#urls) URL.revokeObjectURL(url)
    this.#urls.clear()
    this.#images.clear()
  }

  async #initialize() {
    try {
      return await this.#request('initialize', {source: this.source, profile: this.profile}).promise
    } catch (error) {
      this.#stop(errorFrom(error))
      throw error
    }
  }

  #request<K extends DocumentOperation>(type: K, input: DocumentOperations[K]['input']) {
    const id = ++this.#nextRequestId
    const completion = Promise.withResolvers<DocumentOperations[K]['output']>()
    const request: Request = {
      type,
      sent: false,
      resolve: response => {
        if (response.ok) completion.resolve(response.value)
        else completion.reject(response.cancelled ? abortError() : new Error(response.error))
      },
      reject: completion.reject,
    }
    this.#requests.set(id, request)
    void this.#send(id, {type, requestId: id, input} as WorkerDocumentMessage)
    return {id, promise: completion.promise}
  }

  async #send(id: number, message: WorkerDocumentMessage) {
    try {
      if (message.type !== 'initialize') await this.ready
      if (!this.#requests.has(id)) return
      if (this.#terminalError || this.#closed) throw this.#terminalError ?? abortError()
      const request = this.#requests.get(id)
      if (request) request.sent = true
      this.#worker.postMessage(message)
    } catch (error) {
      this.#requests.get(id)?.reject(errorFrom(error))
      this.#requests.delete(id)
    }
  }

  async #present(entry: ImageRequest, promise: Promise<DocumentOperations['render']['output']>) {
    try {
      const paint = await promise
      if (entry.settled) return
      const decoder = new Image()
      const url = URL.createObjectURL(paint.blob)
      this.#urls.add(url)
      entry.url = url
      entry.decoder = decoder
      decoder.src = url
      await decoder.decode()
      if (entry.settled) return
      entry.value = {url, width: paint.displayWidth, height: paint.displayHeight}
      entry.settled = true
      entry.resolve(entry.value)
    } catch (error) {
      this.#failImage(entry, errorFrom(error))
    } finally {
      this.#releaseDecoder(entry)
    }
  }

  #releaseDecoder(entry: ImageRequest) {
    entry.decoder?.removeAttribute('src')
    entry.decoder = undefined
  }

  #failImage(entry: ImageRequest, error: Error) {
    if (entry.settled) return
    entry.settled = true
    this.#releaseDecoder(entry)
    if (entry.url) {
      URL.revokeObjectURL(entry.url)
      this.#urls.delete(entry.url)
      entry.url = undefined
    }
    entry.reject(error)
  }

  #cancel(ids: readonly number[]) {
    const sent = ids.filter(id => this.#requests.get(id)?.sent)
    if (sent.length && !this.#terminalError) {
      try {
        this.#worker.postMessage({type: 'cancel', requestIds: sent} satisfies WorkerDocumentMessage)
      } catch (error) {
        this.#stop(errorFrom(error))
      }
    }
    for (const id of ids) {
      this.#requests.get(id)?.reject(abortError())
      this.#requests.delete(id)
    }
  }

  #receive = (event: MessageEvent<DocumentResponse>) => {
    const response = event.data
    if (!response || !Number.isInteger(response.requestId) || typeof response.ok !== 'boolean') {
      this.#stop(new Error('The Taillights worker returned an invalid response.'))
      return
    }
    const request = this.#requests.get(response.requestId)
    if (!request) return
    this.#requests.delete(response.requestId)
    if (response.type !== request.type) {
      request.reject(new Error('The Taillights worker returned a mismatched operation.'))
      return
    }
    request.resolve(response)
  }

  #crash = (event: ErrorEvent) => {
    this.#stop(errorFrom(event.error ?? event.message ?? 'The Taillights worker failed.'))
  }

  #messageError = () => {
    this.#stop(new Error('The Taillights worker response could not be read.'))
  }

  #stop(error: Error) {
    if (this.#terminalError) return
    this.#terminalError = error
    this.#worker.removeEventListener('message', this.#receive)
    this.#worker.removeEventListener('error', this.#crash)
    this.#worker.removeEventListener('messageerror', this.#messageError)
    this.#worker.terminate()
    for (const request of this.#requests.values()) request.reject(error)
    this.#requests.clear()
    for (const entry of this.#images.values()) this.#failImage(entry, error)
    this.#failed.resolve(error)
  }
}

export function createTaillightsController(
  source: SourceDocument,
  profile: RenderingProfile,
  workerFactory?: TaillightsWorkerFactory,
) {
  return new TaillightsController(source, profile, workerFactory)
}
